/**
 * AdminSecurityPanel — FEATURE B: rejected attempts + consumption spikes.
 *
 * Reads GET /admin/security/events and /admin/security/spikes. Kept separate
 * from the node-health panel even though both share the admin screen.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ShieldAlert, RefreshCw, Loader2, Flame, Database, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';

const TYPE_LABELS = {
  insufficient_credits: 'Créditos insuficientes',
  auth_rejected: 'Auth rechazada',
  rate_limited: 'Rate limit',
  banned_user_attempt: 'Usuario baneado',
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('es', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

export default function AdminSecurityPanel() {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [tableMissing, setTableMissing] = useState(false);
  const [missingSql, setMissingSql] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [spikes, setSpikes] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, sp, cfg] = await Promise.all([
        adminService.listSecurityEvents({ eventType: typeFilter, userId: userFilter, limit: 100 }),
        adminService.getConsumptionSpikes(),
        adminService.getSecurityConfig(),
      ]);
      setEvents(ev.events || []);
      setCounts(ev.counts_last_7d || {});
      setTableMissing(!!ev.table_missing);
      setMissingSql(ev.detail?.sql || '');
      setSpikes(sp);
      setConfig(cfg.config);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar los eventos de seguridad');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, userFilter]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await adminService.saveSecurityConfig({
        spike_credits: Number(config.spike_credits),
        spike_window_minutes: Number(config.spike_window_minutes),
      });
      setConfig(res.config);
      toast.success('Umbral actualizado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el umbral');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !events.length && !spikes) {
    return (
      <div className="admin-health-loading" data-testid="security-panel-loading">
        <Loader2 size={18} className="animate-spin" /> Cargando eventos de seguridad…
      </div>
    );
  }

  return (
    <div className="admin-security-panel" data-testid="security-panel">
      <div className="admin-health-head">
        <div className="admin-health-title">
          <ShieldAlert size={15} />
          <span>Intentos rechazados (últimos 7 días)</span>
        </div>
        <button className="admin-refresh" onClick={load} data-testid="security-refresh">
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      {tableMissing && (
        <div className="admin-security-missing" data-testid="security-table-missing">
          <Database size={14} />
          <div>
            <strong>Falta la tabla `security_events`.</strong>
            <p>Ejecutá este SQL una sola vez en el editor de Supabase y recargá:</p>
            <pre>{missingSql}</pre>
          </div>
        </div>
      )}

      <div className="admin-health-summary">
        {Object.keys(TYPE_LABELS).map((t) => (
          <div key={t} className="admin-health-chip" data-testid={`security-count-${t}`}>
            <strong>{counts[t] ?? 0}</strong>
            <span>{TYPE_LABELS[t]}</span>
          </div>
        ))}
      </div>

      <div className="admin-security-filters">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="security-type-filter"
        >
          <option value="">Todos los tipos</option>
          {Object.keys(TYPE_LABELS).map((t) => (
            <option key={t} value={t}>{TYPE_LABELS[t]}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Filtrar por user_id"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          data-testid="security-user-filter"
        />
      </div>

      <div className="admin-security-table" data-testid="security-events-table">
        <div className="admin-security-row head">
          <span>Usuario</span><span>Tipo</span><span>Motivo</span><span>Endpoint</span><span>Cuándo</span>
        </div>
        {events.length === 0 ? (
          <div className="admin-security-empty" data-testid="security-events-empty">
            Sin intentos rechazados registrados.
          </div>
        ) : events.map((ev) => (
          <div className="admin-security-row" key={ev.id} data-testid={`security-event-${ev.id}`}>
            <span className="mono">{ev.user_id || '—'}</span>
            <span className={`admin-security-type ${ev.event_type}`}>
              {TYPE_LABELS[ev.event_type] || ev.event_type}
            </span>
            <span title={ev.reason || ''}>{ev.reason || '—'}</span>
            <span className="mono">{ev.endpoint || '—'}</span>
            <span>{fmtWhen(ev.created_at)}</span>
          </div>
        ))}
      </div>

      {/* ── Consumption spikes ── */}
      <div className="admin-health-head spikes">
        <div className="admin-health-title">
          <Flame size={15} />
          <span>Picos de consumo</span>
          <em>
            &gt; {spikes?.threshold?.credits} créditos en {spikes?.threshold?.window_minutes} min
            (últimas {spikes?.threshold?.lookback_hours} h)
          </em>
        </div>
      </div>

      {config && (
        <div className="admin-security-threshold" data-testid="security-threshold-form">
          <label>
            Créditos
            <input
              type="number" min="1"
              value={config.spike_credits}
              onChange={(e) => setConfig({ ...config, spike_credits: e.target.value })}
              data-testid="security-threshold-credits"
            />
          </label>
          <label>
            Minutos
            <input
              type="number" min="1"
              value={config.spike_window_minutes}
              onChange={(e) => setConfig({ ...config, spike_window_minutes: e.target.value })}
              data-testid="security-threshold-minutes"
            />
          </label>
          <button onClick={saveConfig} disabled={saving} data-testid="security-threshold-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar
          </button>
        </div>
      )}

      <div className="admin-security-table" data-testid="security-spikes-table">
        <div className="admin-security-row head spikes">
          <span>Usuario</span><span>Créditos</span><span>Eventos</span><span>Desde</span><span>Hasta</span>
        </div>
        {(spikes?.spikes || []).length === 0 ? (
          <div className="admin-security-empty" data-testid="security-spikes-empty">
            Ningún usuario superó el umbral configurado.
          </div>
        ) : spikes.spikes.map((s) => (
          <div className="admin-security-row spikes" key={s.user_id} data-testid={`security-spike-${s.user_id}`}>
            <span className="mono">{s.user_id}</span>
            <span className="admin-spike-credits">{s.credits}</span>
            <span>{s.events}</span>
            <span>{fmtWhen(s.from)}</span>
            <span>{fmtWhen(s.to)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
